/**
 * The Diurnal QC screen's keyboard shortcuts (sign-off 02-042): the Diurnal tool's keys
 * (`setupKeyboardShortcutInterceptor`, tauri-bridge.js:626-688; inventory 02 §8.1) for what this screen does. One
 * list for the screen, its button titles and the help. Skipped while typing in a box, as the tool. Keys of features
 * Teslyn Field does not have on this screen are not taken: Ctrl+K (weekly PDF, decision 57: not migrated), F5
 * (decision 312: it keeps its usual reload role; TA Live auto-refresh is the panel's icon), Ctrl+I, Ctrl+R, Ctrl+O,
 * Ctrl+U, F1 (Teslyn Field's app-level list, `app-commands.ts`, parity 06-010). The tool's Clear Data
 * (`menu-clear-data`, Ctrl+Shift+C) is the screen's "Clear the charts" button, with no key (decision 725).
 */

export type DiurnalCommand =
  | 'screenshot_save'
  | 'screenshot_copy'
  | 'daily_pdf'
  | 'workbook'
  | 'data_primary'
  | 'data_all'
  | 'export_all'
  | 'audio_toggle'
  | 'alarm_test'
  | 'alerts'
  /** The INTERMAGNET station map (the tool's Global Stations > Station Map…, Ctrl+G; MB request R21). */
  | 'station_map';

export interface DiurnalShortcut {
  /** As written in titles and help: `Ctrl+Shift+B`. */
  readonly keys: string;
  readonly key: string;
  readonly shift: boolean;
  readonly command: DiurnalCommand;
  readonly label: string;
}

const s = (key: string, shift: boolean, command: DiurnalCommand, label: string): DiurnalShortcut => ({
  keys: `Ctrl+${shift ? 'Shift+' : ''}${key.toUpperCase()}`,
  key,
  shift,
  command,
  label,
});

export const DIURNAL_SHORTCUTS: readonly DiurnalShortcut[] = [
  s('s', false, 'screenshot_save', 'Save screenshot'),
  s('s', true, 'screenshot_copy', 'Copy screenshot to the clipboard'),
  s('d', false, 'daily_pdf', 'Daily PDF'),
  s('j', false, 'workbook', 'Exceedance workbook'),
  s('b', false, 'data_primary', 'Export .raw and .xyz'),
  s('b', true, 'data_all', 'Export all basemags'),
  s('e', false, 'export_all', 'Export all'),
  s('m', false, 'audio_toggle', 'Alarm sound on or off'),
  s('t', false, 'alarm_test', 'Test alarm'),
  s('l', false, 'alerts', 'Go to the alerts'),
  s('g', false, 'station_map', 'INTERMAGNET station map'),
];

/** Input types that take no typing (the shortcuts still work there). */
const NOT_TYPING: ReadonlySet<string> = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'range',
  'color',
]);

/** The element a key press came from, as far as the shortcuts care. */
export interface ShortcutTarget {
  readonly tagName?: string;
  readonly type?: string | undefined;
  readonly isContentEditable?: boolean;
}

/**
 * True while the focus is in a text box, a text area or editable text: typing keeps its own keys there (the tool's
 * rule). A tick box or a button is not typing.
 */
export function isTyping(target?: ShortcutTarget | null): boolean {
  const tag = (target?.tagName ?? '').toUpperCase();
  return (
    (tag === 'INPUT' && !NOT_TYPING.has((target?.type ?? 'text').toLowerCase())) ||
    tag === 'TEXTAREA' ||
    target?.isContentEditable === true
  );
}

/** The shortcut of a command (`Ctrl+D`), for button titles. */
export function shortcutKeys(command: DiurnalCommand): string {
  return DIURNAL_SHORTCUTS.find((x) => x.command === command)?.keys ?? '';
}

/**
 * The command of a key press, or null. Ctrl (or Cmd) with the key, Shift as listed, never Alt; nothing while the
 * focus is in a text box, a text area or editable text, so typing keeps its own keys (the tool's rule). A tick box
 * or a button keeps the shortcuts: the tool's native menu accelerators fired there too.
 */
export function diurnalShortcut(
  e: {
    readonly key: string;
    readonly ctrlKey: boolean;
    readonly metaKey: boolean;
    readonly shiftKey: boolean;
    readonly altKey: boolean;
  },
  target?: ShortcutTarget | null,
): DiurnalCommand | null {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  if (isTyping(target)) return null;
  const k = e.key.toLowerCase();
  return DIURNAL_SHORTCUTS.find((x) => x.key === k && x.shift === e.shiftKey)?.command ?? null;
}
